import { Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { displayRange, fromDisplay, toDisplay, unitLabel } from '../format';
import { useNumberText } from './NumberControl';
import type { ControlProps } from './types';

/** A whole number with − and + either side. */
export function StepperControl({ id, setting, value, onChange, disabled, invalid, describedBy }: ControlProps) {
  const [text, change] = useNumberText(setting, value);
  const { min, max, step } = displayRange(setting);
  const unit = unitLabel(setting);
  const current = typeof value === 'number' && Number.isFinite(value) ? toDisplay(setting, value) : (min ?? 0);
  const clamp = (v: number) => Math.min(max ?? Infinity, Math.max(min ?? -Infinity, v));
  const nudge = (dir: 1 | -1) => onChange(fromDisplay(setting, clamp(Number((current + dir * step).toFixed(10)))));
  return (
    <div className="flex items-center gap-1.5">
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-8 w-8"
        aria-label={`Decrease ${setting.ui.title}`}
        disabled={disabled || (min != null && current <= min)}
        onClick={() => nudge(-1)}
      >
        <Minus />
      </Button>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        step={step}
        min={min}
        max={max}
        value={text}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onChange={(e) => onChange(change(e.target.value))}
        className="h-8 w-16 text-center tabular-nums"
      />
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-8 w-8"
        aria-label={`Increase ${setting.ui.title}`}
        disabled={disabled || (max != null && current >= max)}
        onClick={() => nudge(1)}
      >
        <Plus />
      </Button>
      {unit ? <span className="ml-1 text-xs text-muted-foreground">{unit}</span> : null}
    </div>
  );
}
